import { ProjectResponse, UserResponse } from '@ydistri/identity-sdk';
import { CalculationsTableFilters } from '../screens/Calculations/calculationsTypes';

export type SelectedTemplatesForProject = Record<string, number | undefined>;
export type ProjectInfo = Record<string, ProjectResponse | undefined>;
export type ProjectCalculationTableFilters = Record<
  string,
  Partial<CalculationsTableFilters> | undefined
>;
export type ConfigurationSectionOpenStateByProject = Record<
  string,
  Record<string, boolean | undefined> | undefined
>;

export interface YbotDockState {
  isOpen: boolean;
  /** In pixels, already clamped to the range the dock allows. */
  height: number;
}

/**
 * Keyed by project **and** user, because two people share a browser profile on the demo machines and
 * the dock is a per-person surface. See {@link ybotDockStateKey}.
 */
export type YbotDockStateByProjectAndUser = Record<string, YbotDockState | undefined>;

export enum LSKey {
  TOKEN = '1',
  TOKEN_EXPIRATION = '4',
  MENU_COLLAPSED = '2',
  USER = '5',
  SELECTED_TEMPLATES_FOR_PROJECT = '7',
  PROJECTS_BY_SHORT_NAME = '9',
  CALCULATION_TABLE_FILTERS = '10',
  REFRESH_TOKEN = '11',
  MSAL_ACCOUNT_HOME_ID = '12',
  CONFIGURATION_SECTION_OPEN_STATE = '13',
  SELECTED_PROJECT_SHORT_NAME = '14',
  YBOT_DOCK_STATE = '15',
}

/**
 * Storage ids retired by the project short-name identity migration ('3' selected project GUID, '8'
 * projects by GUID) and by the per-tab project identity fix ('6' selected template id - one scalar
 * shared by every tab, so a tab could adopt another tab's template; the per-project map under '7' is
 * the only source now).
 */
const retiredLSIds = ['3', '8', '6'];

export interface LSValues {
  [LSKey.TOKEN]?: string;
  [LSKey.TOKEN_EXPIRATION]?: string;
  [LSKey.MENU_COLLAPSED]?: boolean;
  [LSKey.USER]?: UserResponse;
  [LSKey.SELECTED_TEMPLATES_FOR_PROJECT]?: SelectedTemplatesForProject;
  [LSKey.PROJECTS_BY_SHORT_NAME]?: ProjectInfo;
  [LSKey.CALCULATION_TABLE_FILTERS]?: ProjectCalculationTableFilters;
  [LSKey.REFRESH_TOKEN]?: string;
  [LSKey.MSAL_ACCOUNT_HOME_ID]?: string;
  [LSKey.CONFIGURATION_SECTION_OPEN_STATE]?: ConfigurationSectionOpenStateByProject;
  [LSKey.SELECTED_PROJECT_SHORT_NAME]?: string;
  [LSKey.YBOT_DOCK_STATE]?: YbotDockStateByProjectAndUser;
}

/** One-time hygiene: drop values stored under retired (GUID-based) keys. */
export function removeRetiredItems(): void {
  retiredLSIds.forEach(id => localStorage.removeItem(id));
}

export function setItem<T extends LSKey>(key: T, value: LSValues[T]): void {
  if (value !== undefined) {
    localStorage.setItem(key, JSON.stringify(value));
  } else {
    localStorage.removeItem(key);
  }
}

export function removeItem(key: LSKey): void {
  localStorage.removeItem(key);
}

export function getItem<T extends LSKey>(key: T): LSValues[T] | undefined {
  const savedItem = localStorage.getItem(key);
  if (savedItem && savedItem !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
    return JSON.parse(savedItem) as LSValues[T];
  }
  return undefined;
}

export function setProjectInfo(projects: ProjectResponse[]): void {
  const projectInfoByShortName: ProjectInfo = {};
  projects.forEach(p => {
    projectInfoByShortName[p.shortName] = p;
  });
  setItem(LSKey.PROJECTS_BY_SHORT_NAME, projectInfoByShortName);
}

export function setSelectedTemplateForProject(
  projectShortName: string,
  templateId: number | undefined,
): void {
  const t = getItem(LSKey.SELECTED_TEMPLATES_FOR_PROJECT);
  if (t) {
    t[projectShortName] = templateId;
    setItem(LSKey.SELECTED_TEMPLATES_FOR_PROJECT, t);
  } else {
    setItem(LSKey.SELECTED_TEMPLATES_FOR_PROJECT, {
      [projectShortName]: templateId,
    });
  }
}

export function setProjectCalculationTableFiltersForProject(
  projectShortName: string,
  data: Partial<CalculationsTableFilters>,
): void {
  const t = getItem(LSKey.CALCULATION_TABLE_FILTERS);
  if (t) {
    t[projectShortName] = { ...t[projectShortName], ...data };
    setItem(LSKey.CALCULATION_TABLE_FILTERS, t);
  } else {
    setItem(LSKey.CALCULATION_TABLE_FILTERS, {
      [projectShortName]: { ...data },
    });
  }
}

export function getSelectedTemplateForProject(
  projectShortName: string | undefined,
): number | undefined {
  if (!projectShortName) return undefined;
  return getItem(LSKey.SELECTED_TEMPLATES_FOR_PROJECT)?.[projectShortName];
}

export function getConfigurationSectionOpenStateForProject(
  projectShortName: string | undefined,
  sectionId: string,
): boolean | undefined {
  if (!projectShortName) {
    return undefined;
  }
  return getItem(LSKey.CONFIGURATION_SECTION_OPEN_STATE)?.[projectShortName]?.[sectionId];
}

export function setConfigurationSectionOpenStateForProject(
  projectShortName: string | undefined,
  sectionId: string,
  isOpen: boolean,
): void {
  if (!projectShortName) {
    return;
  }

  const state = getItem(LSKey.CONFIGURATION_SECTION_OPEN_STATE) ?? {};
  setItem(LSKey.CONFIGURATION_SECTION_OPEN_STATE, {
    ...state,
    [projectShortName]: {
      ...state[projectShortName],
      [sectionId]: isOpen,
    },
  });
}

/**
 * The map key for one person's dock on one project. Both parts are required: without the project the
 * dock would follow the user across projects it was never opened in, and without the user it would
 * follow whoever logs in next on the same browser profile.
 */
const ybotDockStateKey = (projectShortName: string, userId: string): string =>
  `${projectShortName}|${userId}`;

export function getYbotDockState(
  projectShortName: string | undefined,
  userId: string | undefined,
): YbotDockState | undefined {
  if (!projectShortName || !userId) {
    return undefined;
  }

  return getItem(LSKey.YBOT_DOCK_STATE)?.[ybotDockStateKey(projectShortName, userId)];
}

export function setYbotDockState(
  projectShortName: string | undefined,
  userId: string | undefined,
  state: YbotDockState,
): void {
  if (!projectShortName || !userId) {
    return;
  }

  const stored = getItem(LSKey.YBOT_DOCK_STATE) ?? {};
  setItem(LSKey.YBOT_DOCK_STATE, {
    ...stored,
    [ybotDockStateKey(projectShortName, userId)]: state,
  });
}

const localStoreListenersByKey = new Map<LSKey, Set<() => void>>();
const localStoreKeys = new Set<string>(Object.values(LSKey));

const isLSKey = (key: string): key is LSKey => {
  return localStoreKeys.has(key);
};

const handleLocalStoreStorageChange = (event: StorageEvent): void => {
  if (event.key === null) {
    localStoreListenersByKey.forEach(listeners => {
      listeners.forEach(listener => listener());
    });
    return;
  }

  if (!isLSKey(event.key)) {
    return;
  }

  const listeners = localStoreListenersByKey.get(event.key);
  listeners?.forEach(listener => listener());
};

const ensureLocalStoreStorageListener = (): void => {
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleLocalStoreStorageChange);
  }
};

const cleanupLocalStoreStorageListener = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const hasAnyListeners = Array.from(localStoreListenersByKey.values()).some(
    listeners => listeners.size > 0,
  );
  if (!hasAnyListeners) {
    window.removeEventListener('storage', handleLocalStoreStorageChange);
  }
};

/**
 * Subscribes to updates of a single localStorage-backed key.
 * Notifies on both same-tab updates (via notifyLocalStoreKeyUpdated) and
 * cross-tab updates (via the browser "storage" event).
 */
export function subscribeToLocalStoreKey(key: LSKey, listener: () => void): () => void {
  const listeners = localStoreListenersByKey.get(key) ?? new Set<() => void>();
  listeners.add(listener);
  localStoreListenersByKey.set(key, listeners);
  ensureLocalStoreStorageListener();

  return () => {
    const listenersForKey = localStoreListenersByKey.get(key);
    if (!listenersForKey) {
      return;
    }

    listenersForKey.delete(listener);
    if (listenersForKey.size === 0) {
      localStoreListenersByKey.delete(key);
    }
    cleanupLocalStoreStorageListener();
  };
}

/**
 * Notifies all in-tab subscribers that the given localStorage key was updated.
 * Needed because "storage" events are not fired in the same tab that wrote the value.
 */
export function notifyLocalStoreKeyUpdated(key: LSKey): void {
  const listeners = localStoreListenersByKey.get(key);
  listeners?.forEach(listener => listener());
}
